import { catalogLanguageFor, fallbackLanguage, type Language } from './languages'

/** A culture and time zone from one source; either may be unset. */
export interface LocalePreference {
  /** A BCP 47 tag, such as `fr-CA`. As in legacy, it sets both the language and the formats. */
  readonly culture: string | null
  /** An IANA time zone, such as `Europe/Paris`. */
  readonly timeZone: string | null
}

/** What the browser offers: its languages, most preferred first, and its time zone. */
export interface BrowserLocale {
  readonly cultures: readonly string[]
  readonly timeZone: string | null
}

/** Where the settings come from, in precedence order. A source is `null` until it is known. */
export interface LocaleSources {
  /** The person's own settings. Filled by Site Management. */
  readonly person: LocalePreference | null
  /** The site's culture and time zone. Filled by Site Management. */
  readonly site: LocalePreference | null
  readonly browser: BrowserLocale
}

/** The settings in effect. */
export interface LocaleSettings {
  /** The catalog the text comes from. */
  readonly language: Language
  /** What dates and numbers are formatted for. A culture outside the catalog keeps its formats. */
  readonly locale: string
  /** The IANA time zone dates and times are shown in. */
  readonly timeZone: string
}

/** Reads the browser's languages and time zone. */
export function readBrowserLocale(): BrowserLocale {
  return {
    cultures: navigator.languages.length > 0 ? navigator.languages : [navigator.language],
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }
}

/**
 * Picks the culture and the time zone separately, each from the person's settings, then the
 * site's, then the browser's. A value `Intl` cannot use is skipped.
 */
export function resolveLocaleSettings(sources: LocaleSources): LocaleSettings {
  const locale =
    canonicalCulture(sources.person?.culture)
    ?? canonicalCulture(sources.site?.culture)
    ?? browserCulture(sources.browser.cultures)
    ?? fallbackLanguage

  const timeZone =
    validTimeZone(sources.person?.timeZone)
    ?? validTimeZone(sources.site?.timeZone)
    ?? validTimeZone(sources.browser.timeZone)
    ?? 'UTC'

  return { language: catalogLanguageFor(locale) ?? fallbackLanguage, locale, timeZone }
}

/** The tag the text is in: the locale when it is the catalog's language, else the language. */
export function textLocale(settings: LocaleSettings): string {
  return catalogLanguageFor(settings.locale) === settings.language
    ? settings.locale
    : settings.language
}

/** The first browser culture the catalog has; failing that, the first one, for its formats. */
function browserCulture(cultures: readonly string[]): string | null {
  const valid = cultures
    .map((culture) => canonicalCulture(culture))
    .filter((culture) => culture !== null)

  return valid.find((culture) => catalogLanguageFor(culture) !== null) ?? valid[0] ?? null
}

/** The canonical tag, or `null` when it is malformed or `Intl` cannot format for it. */
function canonicalCulture(culture: string | null | undefined): string | null {
  if (culture === null || culture === undefined || culture.trim() === '') {
    return null
  }

  let canonical: string | undefined
  try {
    canonical = Intl.getCanonicalLocales(culture)[0]
  } catch {
    return null
  }

  // Well-formed is not enough: `zz-ZZ` parses, but would format in the runtime's default.
  if (
    canonical === undefined
    || Intl.DateTimeFormat.supportedLocalesOf(canonical).length === 0
    || Intl.NumberFormat.supportedLocalesOf(canonical).length === 0
  ) {
    return null
  }
  return canonical
}

/** The time zone's canonical IANA id, or `null` when it is not one. */
export function validTimeZone(timeZone: string | null | undefined): string | null {
  if (timeZone === null || timeZone === undefined || timeZone.trim() === '') {
    return null
  }
  try {
    return new Intl.DateTimeFormat('en', { timeZone }).resolvedOptions().timeZone
  } catch {
    return null
  }
}
